/**
 * Thanh trượt giá hai đầu cho bộ lọc collection.
 *
 * Theme Horizon chỉ có hai ô nhập số cho khoảng giá. Phần tử này thêm thanh kéo
 * nằm ngay trên hai ô đó và nối vào đúng cơ chế lọc sẵn có, không dựng cơ chế riêng:
 *
 *  - Kéo (sự kiện `input`): chỉ ghi giá trị vào hai ô số và vẽ lại vệt màu. Chưa lọc,
 *    để lưới không nhấp nháy theo từng pixel di chuyển.
 *  - Thả tay (sự kiện `change`): trình duyệt tự bắn `change`, nó nổi bọt lên
 *    `<price-facet-component on:change="/updatePriceFilterAndResults">` — chính handler
 *    mà hai ô số vẫn dùng. Không phải gọi tay, cũng không đụng vào facets.js.
 *
 * Hai `input[type=range]` chồng lên nhau là cách làm thanh hai đầu mà không cần thư
 * viện. Chỉ phần nút tròn nhận chuột (`pointer-events`), phần thân để lọt xuống dưới,
 * nếu không thì thanh nằm trên che mất nút của thanh nằm dưới.
 *
 * Không đặt `name` cho hai range: form lọc chỉ gửi input có `name`, nên chúng vô hình
 * với Shopify, giá trị thật vẫn nằm ở hai ô số.
 */
class PfPriceSlider extends HTMLElement {
  connectedCallback() {
    const facet = this.closest('price-facet-component');
    if (!facet) return;

    this.minRange = this.querySelector('[data-pf-range="min"]');
    this.maxRange = this.querySelector('[data-pf-range="max"]');
    this.minInput = facet.querySelector('[ref="minInput"]');
    this.maxInput = facet.querySelector('[ref="maxInput"]');

    if (!this.minRange || !this.maxRange || !this.minInput || !this.maxInput) return;

    this.floor = Number(this.minRange.min);
    this.ceiling = Number(this.maxRange.max);

    this.readFromInputs();

    this.minRange.addEventListener('input', this.handleDrag);
    this.maxRange.addEventListener('input', this.handleDrag);
    this.minInput.addEventListener('input', this.readFromInputs);
    this.maxInput.addEventListener('input', this.readFromInputs);
  }

  /** Số tiền trong ô có thể là "59.95" hoặc rỗng khi khách chưa lọc gì. */
  parse(value, fallback) {
    const parsed = Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /** Gõ tay vào ô số thì thanh trượt phải chạy theo. */
  readFromInputs = () => {
    const low = this.parse(this.minInput.value, this.floor);
    const high = this.parse(this.maxInput.value, this.ceiling);

    this.minRange.value = String(Math.min(low, high));
    this.maxRange.value = String(Math.max(low, high));
    this.paint();
  };

  handleDrag = (event) => {
    let low = Number(this.minRange.value);
    let high = Number(this.maxRange.value);

    // Hai đầu vượt qua nhau thì đẩy cái đang kéo lại, không đổi chỗ hai đầu —
    // đổi chỗ giữa lúc đang giữ chuột làm nút nhảy khỏi ngón tay.
    if (low > high) {
      if (event.target === this.minRange) {
        low = high;
        this.minRange.value = String(high);
      } else {
        high = low;
        this.maxRange.value = String(low);
      }
    }

    // Chạm đúng hai đầu thì để ô trống: có số là Shopify thêm tham số lọc vào URL,
    // trong khi "từ 0 đến giá cao nhất" vốn không lọc gì cả.
    this.minInput.value = low <= this.floor ? '' : String(low);
    this.maxInput.value = high >= this.ceiling ? '' : String(high);

    this.paint();
  };

  paint() {
    const span = this.ceiling - this.floor || 1;
    const from = ((Number(this.minRange.value) - this.floor) / span) * 100;
    const to = ((Number(this.maxRange.value) - this.floor) / span) * 100;
    this.style.setProperty('--pf-slider-from', `${from}%`);
    this.style.setProperty('--pf-slider-to', `${to}%`);
  }
}

if (!customElements.get('pf-price-slider')) {
  customElements.define('pf-price-slider', PfPriceSlider);
}
